import _ from 'lodash';
import { Visualization } from '@/annotation/ckEditorPlugins/components/content.utilities.constants';
import { isAllContentFinishedLoading } from '@/annotation/contentSelector.utilities';
import { sqPluginStore, sqStateSynchronizer, sqWorkbookStore, sqWorksheetStore } from '@/core/core.stores';
import { PERSISTENCE_LEVELS } from '@/core/flux.service';
import { isSystemTest } from '@/core/utilities';
import { goTo, navigateToHeadlessRenderStandby } from '@/main/routing.utilities';
import { HEADLESS_CHART_RENDERING_CLASS } from '@/markdown/markdown.constants';
import { SCREENSHOT_SIZE_TO_CONTENT } from '@/reportEditor/report.constants';
import { getPendingRequestCount } from '@/requests/axios.utilities';
import { cancelAll } from '@/requests/pendingRequests.utilities';
import { HeadlessCategories, HeadlessJobFormat } from '@/services/headlessCapture.constants';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { onAllServerRequestsCanceled } from '@/services/notifier.service';
import { HEADLESS_AG_RENDERING_CLASS } from '@/tableBuilder/tableBuilder.constants';
import { logError, logInfo } from '@/utilities/logger';
import { formatMessage } from '@/utilities/logger.utilities';
import { close as socketClose, waitForOpen } from '@/utilities/socket.utilities';
import { getStorageSafely } from '@/utilities/storage.utilities';
import type { VisualizationData } from '@/annotation/ckEditorPlugins/components/content.utilities.constants';
import type { HeadlessCategory } from '@/services/headlessCapture.constants';

/**
 * @file Requests and manages screenshots.
 *
 * This service has some functions which only run when the app is running in the headless browser used by
 *   NodeJS to capture screenshots. The 'callback' mechanism of that browser is used to send
 *   messages to NodeJS to inform it when the screenshot is ready to be taken.
 */

// Screenshot rendering only supports a small subset of paths within the application, if the headless browser is
// redirected to a page that isn't allowed the capture should fail. The load-error page is allowed because it has
// special handling for failing the headless capture with a relevant message.
export const ALLOWED_SCREENSHOT_MODE_PATHS = /^\/(present|headless-capture-standby|load-error|report-template)/;

let captureMetadata: { category?: HeadlessCategory; jobFormat?: HeadlessJobFormat } = {};

/**
 * Tells headless capture browser that the app is ready to capture a screenshot.
 * This function does nothing unless the app is being run in the headless capture browser.
 */
export function notifyCapture() {
  function waitForQuiescence() {
    return Promise.all([waitForAppQuiescence(), waitForPluginQuiescence()]);
  }

  if (headlessRenderMode() || isSystemTest()) {
    // Wait for the app to settle then wait 100ms and wait for the app to settle again. This is an attempt to handle
    // views that request data asynchronously some (short) time after the view is first loaded.
    waitForQuiescence()
      .then(() => {
        return new Promise((resolve) => {
          setTimeout(resolve, 100);
        });
      })
      .then(waitForQuiescence)
      .then(() => {
        window.seeqHeadlessCapture?.();
        // @ts-ignore Used by playwright to know when page is settled
        window.isSeeqPageSettled = true;
      });
  }
}

/**
 * Tells the headless capture browser that the page is loading.
 * This function does nothing unless the app is being run in the headless capture browser.
 */
export function notifyLoading() {
  if (headlessRenderMode()) {
    window.seeqHeadlessLoading();
  }
}

/**
 * Tells the headless capture browser that the page encountered an unrecoverable error
 * This function does nothing unless the app is being run in the headless capture browser.
 */
export function notifyError(...errors: string[]) {
  if (headlessRenderMode()) {
    window.seeqHeadlessError(...errors);
  }
}

export function notifyWarning(warning: string) {
  if (headlessRenderMode()) {
    window.seeqHeadlessWarning(warning);
  }
}

/**
 * Tells the headless capture browser that the currently-loading workbook has been cancelled by a user or
 * administrator.  This function does nothing unless the app is being run in the headless capture browser.
 */
export function notifyCancellation(...errors: string[]) {
  if (headlessRenderMode()) {
    window.seeqHeadlessCancellation(...errors);
  }
}

/**
 * Gets the category of headless capture being requested
 */
export function headlessCaptureMetadata() {
  return captureMetadata;
}

/**
 * Indicates what category of job this headless browser page is mapped to.
 *
 * @returns the category of the job for which this page is rendering. If this page is not rendering in a headless
 *   browser, then this function returns undefined
 */
export function headlessRenderCategory(): string | undefined {
  return headlessCaptureMetadata().category;
}

const DEFAULT_VISUALIZATION: VisualizationData = { visualization: Visualization.NONE };
// Exposed for testing
export const DEFAULT_GET_VISUALIZATION_DATA = () => Promise.resolve(DEFAULT_VISUALIZATION);

/**
 * Attaches callbacks to window that puppeteer can call as well as a state change handler that closes the socket
 * and clears local storage. Does nothing if not in screenshot render mode. See custom-typings.d.ts (Window) for
 * more detailed description of the callbacks attached
 */
export function initializeHeadlessCaptureMode() {
  if (!headlessRenderMode()) {
    return;
  }

  window.seeqGetVisualizationData = DEFAULT_GET_VISUALIZATION_DATA;

  // Provides a hook that the headless capture browser can use to cancel all requests from this browser
  window.seeqInternalPageCleanup = () => {
    navigateToHeadlessRenderStandby();
    window.seeqGetVisualizationData = DEFAULT_GET_VISUALIZATION_DATA;
    logInfo("Cleaning up internal state via 'window.seeqInternalCleanup'");
    return (
      cancelAll()
        .catch((e) => logError(formatMessage`Error cancelling all requests: ${e}`))
        // Not all screenshots require the socket to be open, but the socket service will get into a bad state if
        // the socket is closed before it is fully established - to prevent this wait for the socket to be open before
        // capturing the screenshot
        .then(() => waitForOpen())
        .catch((e) => logError(formatMessage`Error waiting for socket to open: ${e}`))
        .then(() => {
          socketClose();
          getStorageSafely().clear();

          // Re-initialize all stores. This is necessary since some stores have special logic to retain certain
          /* information even when they are rehydrated; search for 'saveState' for examples. */ _.forEach(
            PERSISTENCE_LEVELS,
            (persistenceLevel) => {
              sqStateSynchronizer.initialize(persistenceLevel);
            },
          );
        })
        .catch((e) => logError(formatMessage`Error cleaning up page state: ${e}`))
    );
  };

  window.seeqNavigate = (url) => {
    if (!_.isString(url) || !_.startsWith(url, '/')) {
      throw new Error(`seeqNavigate: A relative url is required, but got '${url}'`);
    }
    if (url && !ALLOWED_SCREENSHOT_MODE_PATHS.test(url.replace(/^#!/, ''))) {
      notifyError(`Could not load path not allowed in screenshot render mode: ${url}`);
    }
    setTimeout(() => {
      goTo(url);
    }, 10);
  };

  onAllServerRequestsCanceled(() => {
    notifyError('Administrator canceled all requests');
  });
}

/**
 * Fetches the category of headless capture being requested so that it can be accessed synchronously in the page load
 */
export async function fetchHeadlessCaptureMetadata() {
  if (!headlessRenderMode()) {
    return Promise.resolve();
  }

  const [category, jobFormat] = await Promise.all([window.seeqHeadlessCategory(), window.seeqJobFormat()]);
  if (!isHeadlessCategory(category)) {
    throw new Error(`Error: '${category}' category is unexpected; the frontend enum likely needs to be updated`);
  }
  if (!isHeadlessJobFormat(jobFormat)) {
    throw new Error(`Error: '${jobFormat}' jobFormat is unexpected; the frontend enum likely needs to be updated`);
  }
  captureMetadata = {
    category,
    jobFormat,
  };
}

/**
 * Test if {@param category} is a HeadlessCategory
 */
function isHeadlessCategory(category: string): category is HeadlessCategory {
  return HeadlessCategories.includes(category);
}

/**
 * Test if {@param jobFormat} is a HeadlessJobFormat
 */
function isHeadlessJobFormat(jobFormat: unknown): jobFormat is HeadlessJobFormat {
  return _.isString(jobFormat) && Object.values<string>(HeadlessJobFormat).includes(jobFormat);
}

/**
 * Waits for Seeq to settle using the following checks. This isn't easy because not only does all the data need to
 * be fetched and React finish rendering, but other libraries, such as Highcharts and React need to finish resizing
 * and the browser finish painting. The following have to be true in order for the promise to resolve:
 * - no outstanding http requests
 * - all Topic content has finished loading (or errored).
 * - all ag-grid tables have finished rendering
 * - The timeout loop needs to finish in the expected time. If the setTimeout is not executing on time then the
 * event loop in the browser is still busy getting the page settled.
 *
 * @return Resolves when the app has stabilized
 */
export function waitForAppQuiescence(): Promise<void> {
  const TIMEOUT_MILLIS = 100;
  // Allow for a small amount of drift. These numbers based on watching actual numbers on heavy documents
  const TIMEOUT_DURATION_MIN = 90;
  const TIMEOUT_DURATION_MAX = 120;
  return new Promise((resolve) => waitUntilSeeqSettled(resolve, performance.now()));

  function waitUntilSeeqSettled(resolve: (v?: any) => void, lastRunTime: number) {
    const currentRunTime = performance.now();
    const actualTimeoutDuration = currentRunTime - lastRunTime;
    const isEventLoopOnTime =
      actualTimeoutDuration >= TIMEOUT_DURATION_MIN && actualTimeoutDuration <= TIMEOUT_DURATION_MAX;
    if (
      getPendingRequestCount() === 0 &&
      isAllContentFinishedLoading() &&
      areAllAgGridsFinished() &&
      areAllChartsFinished() &&
      areAllImagesLoaded() &&
      isEventLoopOnTime
    ) {
      setTimeout(
        () => {
          removeHeightFromCKTables();
          updateAgGridSizing();
          resolve();
        },
        !!getAgGridElement() && headlessCaptureMetadata().jobFormat === HeadlessJobFormat.PNG ? 100 : 1,
      );
      return;
    }
    setTimeout(() => waitUntilSeeqSettled(resolve, currentRunTime), TIMEOUT_MILLIS);
  }
}

/**
 * Determines if all img tags have finished loading. Needed for creating PDFs of topics. Also handles removing the
 * loading attribute from images that are configured to load lazily so that images below the fold are also loaded.
 */
function areAllImagesLoaded() {
  const imagesNotLoaded = Array.from(document.querySelectorAll('img')).filter(
    (el) => window.getComputedStyle(el).display !== 'none' && !el.complete,
  );
  imagesNotLoaded.forEach((el) => {
    el.removeAttribute('loading');
  });
  return imagesNotLoaded.length === 0;
}

function areAllAgGridsFinished() {
  const tableSelector = document.querySelector(SCREENSHOT_SIZE_TO_CONTENT.SELECTOR);
  // This function gets called when in headless render mode and when rendering the page in a system test. We only
  // need to care about this in headless render mode though, so if not in headless render mode, just return true.
  const isTableSelectorPresent = headlessRenderMode() ? sqWorksheetStore.view.key !== 'TABLE' || !!tableSelector : true;

  return document!.querySelectorAll(`.${HEADLESS_AG_RENDERING_CLASS}`).length === 0 && isTableSelectorPresent;
}

/**
 * Determines if all markdown charts (using Highcharts) have finished rendering.
 * Used in headless capture mode to ensure charts are fully loaded before PDF generation.
 * Charts add the HEADLESS_CHART_RENDERING_CLASS when mounting and remove it when the
 * Highcharts 'load' event fires.
 *
 * @returns {boolean} true if no charts are currently rendering
 */
function areAllChartsFinished(): boolean {
  if (!document) {
    return true; // Defensive: if no document, don't block
  }
  return document.querySelectorAll(`.${HEADLESS_CHART_RENDERING_CLASS}`).length === 0;
}

/**
 * Ag-Grid has a variety of resizing options which can be difficult to manage. Due to us using our own custom
 * styling on top of the table, when grouping and using the domLayout 'print' mode, the groups sometimes will get
 * smooshed together. To work around this, we use the `autoHeight` mode and suppress row virtualization, forcing all
 * of the rows to be rendered. This causes another problem wherein Ag-Grid takes up the full width of the screen
 * rather than just the table width, due to differences in styling between 'print' and 'autoHeight'. Working around
 * that inside the table builder component requires us to set and unset the width as it resizes multiple times on
 * load, which over complicates the component. Instead, we can wait until everything has settled and then update the
 * width once, right here.
 */
function updateAgGridSizing() {
  if (!sqWorkbookStore.isTopic && headlessRenderMode()) {
    const maybeAgGrid = getAgGridElement();
    if (maybeAgGrid) {
      // Headers might be disabled but there might be no rows at all so just take the minimum of all possible values
      const viewportWidth =
        (maybeAgGrid.querySelector('.ag-header-viewport') as HTMLElement)?.offsetWidth ?? Number.MAX_SAFE_INTEGER;
      const containerWidth =
        (maybeAgGrid.querySelector('.ag-header-container') as HTMLElement)?.offsetWidth ?? Number.MAX_SAFE_INTEGER;
      const rowWidth =
        (maybeAgGrid.querySelector('.ag-center-cols-container') as HTMLElement)?.offsetWidth ?? Number.MAX_SAFE_INTEGER;

      const newWidth = Math.min(viewportWidth, containerWidth, rowWidth);
      maybeAgGrid.style.width = `${newWidth}px`;
    }
  }
}

function getAgGridElement(): HTMLElement | null {
  return document.querySelector('.ag-root-wrapper');
}

/**
 * CKEditor tables that have a manually set height cause problems when exporting to PDF because the table
 * height is treated differently due to the various PDF styles that get enabled. We can remove the height and use
 * the natural height of the element instead.
 */
function removeHeightFromCKTables() {
  document.querySelectorAll('figure.table.ck-widget').forEach((el) => {
    if ((el as HTMLElement)?.style?.height) {
      (el as HTMLElement).style.height = '';
    }
  });
}

/**
 * Waits for any visible display pan plugins to complete their rendering
 *
 * @return {Promise} that resolves when all visible display pane plugins have completed their rendering
 */
export function waitForPluginQuiescence(): Promise<void> {
  return new Promise<void>((resolve) => waitUntilPluginRenderComplete(resolve));

  function waitUntilPluginRenderComplete(resolve: (value?: any) => void) {
    const keepWaiting = () => setTimeout(_.partial(waitUntilPluginRenderComplete, resolve), 200);

    if (sqPluginStore.displayPaneRenderComplete) {
      setTimeout(resolve, 1);
    } else {
      keepWaiting();
    }
  }
}
